import { useEffect } from 'react';

export const useGoogleAutocomplete = ({
    selector,
    form,
    enabled = true,
}: {
    selector: string;
    form: any;
    enabled?: boolean;
}) => {
    useEffect(() => {
        // SSR safety (Next.js)
        if (typeof window === 'undefined') return;

        const isGoogleEnabled =
            process.env.NEXT_PUBLIC_ENABLE_GOOGLE_ADDRESS_VALIDATION === 'true';

        if (!enabled || !isGoogleEnabled) return;

        const timer = setTimeout(() => {
            const input = document.querySelector(selector) as HTMLInputElement;

            const googleObj = (window as any).google;

            // Safe checks (fix TS + runtime)
            if (
                !googleObj ||
                !googleObj.maps ||
                !googleObj.maps.places ||
                !input ||
                input.dataset.autocompleteAttached
            ) {
                return;
            }

            // Prevent duplicate initialization
            input.dataset.autocompleteAttached = 'true';

            // Prevent browser autofill conflict
            input.setAttribute('autocomplete', 'off');

            const autocomplete = new googleObj.maps.places.Autocomplete(input, {
                types: ['address'],
                componentRestrictions: { country: 'us' },
            });

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();

                if (!place?.address_components) return;

                let streetNumber = '';
                let route = '';
                let city = '';
                let state = '';
                let zip = '';
                let apartment = '';

                place.address_components.forEach((component: any) => {
                    const types = component.types;

                    if (types.includes('street_number')) streetNumber = component.long_name;
                    if (types.includes('route')) route = component.long_name;
                    if (types.includes('locality')) city = component.long_name;
                    if (!city && types.includes('sublocality')) city = component.long_name;
                    if (types.includes('administrative_area_level_1')) state = component.short_name;
                    if (types.includes('postal_code')) zip = component.long_name;
                    if (types.includes('subpremise')) apartment = component.long_name;
                });

                const street = `${streetNumber} ${route}`.trim();

                // Detect prefix dynamically
                let prefix = '';

                if (selector.includes('billing')) prefix = 'billing';
                else if (selector.includes('shipping')) prefix = 'shipping';
                else if (selector.includes('addressFormValues')) prefix = 'addressFormValues';
                else if (selector.includes('address')) prefix = 'address';

                const values = form.getValues();

                // Dynamic field detection
                const streetKey =
                    values?.[prefix]?.streetAddress !== undefined
                        ? `${prefix}.streetAddress`
                        : values?.[prefix]?.streetName !== undefined
                            ? `${prefix}.streetName`
                            : `${prefix}.streetAddress`;

                const zipKey =
                    values?.[prefix]?.zipCode !== undefined
                        ? `${prefix}.zipCode`
                        : values?.[prefix]?.postalCode !== undefined
                            ? `${prefix}.postalCode`
                            : `${prefix}.zipCode`;

                const apartmentKey =
                    values?.[prefix]?.apartment !== undefined
                        ? `${prefix}.apartment`
                        : values?.[prefix]?.additionalStreetInfo !== undefined
                            ? `${prefix}.additionalStreetInfo`
                            : `${prefix}.apartment`;

                // Delay to avoid modal click/blur conflict
                setTimeout(() => {
                    form.setValue(streetKey, street, {
                        shouldValidate: true,
                        shouldDirty: true,
                    });

                    form.setValue(`${prefix}.city`, city, {
                        shouldValidate: true,
                        shouldDirty: true,
                    });

                    form.setValue(`${prefix}.state`, state, {
                        shouldValidate: true,
                        shouldDirty: true,
                    });

                    form.setValue(zipKey, zip, {
                        shouldValidate: true,
                        shouldDirty: true,
                    });

                    if (apartment) {
                        form.setValue(apartmentKey, apartment, {
                            shouldValidate: true,
                            shouldDirty: true,
                        });
                    }
                }, 100);
            });
        }, 1200); // wait for modal DOM render

        // Cleanup (important for modal reopen)
        return () => {
            clearTimeout(timer);

            const input = document.querySelector(selector) as HTMLInputElement;
            if (input) {
                delete input.dataset.autocompleteAttached;
            }
        };
    }, [selector, enabled, form]);
};
